import React, { useState } from 'react';
import { CustomerApplicationFormModel } from '../types/CustomerApplicationFormModel';
import Input from '../../Inputs/Input';
import { translate } from '../../../Services/translation';
import Checkbox from '../../Inputs/Checkbox';
import { submitCustomerApplicationForm } from '../services/CustomerApplicationFormService';
import EditorString from '../../EditorString/EditorString';

const baseLabel = 'forms.customer.application';
const inputSize = 'large';
const fieldsClassName = 'flex flex-col gap-2';

const ApplicationForm = ({
  model,
}: {
  model: CustomerApplicationFormModel;
}) => {
  const [organizationInputValues, setOrganizationInputValues] = useState<{
    [x: string]: string;
  }>();
  const [billingAddressInputValues, setBillingAddressInputValues] =
    useState<{ [x: string]: string }>();
  const [deliveryAddressInputValues, setDeliveryAddressInputValues] =
    useState<{ [x: string]: string }>();
  const [contactInputValues, setContactInputValues] = useState<{
    [x: string]: string;
  }>();
  const [economyContactEmail, setEconomyContactEmail] = useState<string>();
  const [usePdfInvoice, setUsePdfInvoice] = useState<boolean>(false);
  const [formSuccess, setFormSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const customerApplicationFormApiModel = {
    subject: model.subject,
    form: {
      organization: organizationInputValues,
      billingAddress: billingAddressInputValues,
      deliveryAddress: deliveryAddressInputValues,
      contact: contactInputValues,
      economyContactEmail: economyContactEmail,
    },
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    try {
      var res = await submitCustomerApplicationForm(
        customerApplicationFormApiModel
      );
      if (res.status == 200) {
        setFormSuccess(true);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      } else {
        setErrorMessage(translate(`${baseLabel}.failure`));
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setErrorMessage(translate(`${baseLabel}.failure`));
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const handleOrganizationInputChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.currentTarget;
    setOrganizationInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleBillingAddressInputChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.currentTarget;
    setBillingAddressInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDeliveryAddressInputChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.currentTarget;
    setDeliveryAddressInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleContactInputChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.currentTarget;
    setContactInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleUsePdfInvoiceChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const { name } = e.currentTarget;
    const value = `${!usePdfInvoice}`;
    setContactInputValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setUsePdfInvoice(!usePdfInvoice);
  };

  const organizationInputFields = (
    <div className={fieldsClassName}>
      <Input
        label={translate(`${baseLabel}.organization.name`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="name"
        value={organizationInputValues?.name || ''}
        type="text"
        id="organization-name"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.organization.id`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="id"
        value={organizationInputValues?.id || ''}
        type="text"
        id="organization-id"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.organization.vat`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="vatNumber"
        value={organizationInputValues?.vatNumber || ''}
        type="text"
        id="organization-vat"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.organization.type`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="type"
        value={organizationInputValues?.type || ''}
        type="text"
        id="organization-type"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.organization.turnover`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="turnover"
        value={organizationInputValues?.turnover || ''}
        type="text"
        id="organization-turnover"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.organization.expectedpurchase`)}
        inputSize={inputSize}
        onChange={handleOrganizationInputChange}
        name="expectedPurchase"
        value={organizationInputValues?.expectedPurchase || ''}
        type="text"
        id="organization-expectedpurchase"
        required={true}
      />
    </div>
  );

  const billingAddressInputFields = (
    <div className={fieldsClassName}>
      <Input
        label={translate(`${baseLabel}.billing.street`)}
        inputSize={inputSize}
        onChange={handleBillingAddressInputChange}
        name="street"
        value={billingAddressInputValues?.street || ''}
        type="text"
        id="billing-street"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.billing.city`)}
        inputSize={inputSize}
        onChange={handleBillingAddressInputChange}
        name="city"
        value={billingAddressInputValues?.city || ''}
        type="text"
        id="billing-city"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.billing.zipcode`)}
        inputSize={inputSize}
        onChange={handleBillingAddressInputChange}
        name="zipCode"
        value={billingAddressInputValues?.zipCode || ''}
        type="text"
        id="billing-zipCode"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.billing.country`)}
        inputSize={inputSize}
        onChange={handleBillingAddressInputChange}
        name="country"
        value={billingAddressInputValues?.country || ''}
        type="text"
        id="billing-country"
        required={true}
      />
    </div>
  );

  const deliveryAddressInputFields = (
    <div className={fieldsClassName}>
      <Input
        label={translate(`${baseLabel}.delivery.street`)}
        inputSize={inputSize}
        onChange={handleDeliveryAddressInputChange}
        name="street"
        value={deliveryAddressInputValues?.street || ''}
        type="text"
        id="delivery-street"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.delivery.city`)}
        inputSize={inputSize}
        onChange={handleDeliveryAddressInputChange}
        name="city"
        value={deliveryAddressInputValues?.city || ''}
        type="text"
        id="delivery-city"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.delivery.zipcode`)}
        inputSize={inputSize}
        onChange={handleDeliveryAddressInputChange}
        name="zipCode"
        value={deliveryAddressInputValues?.zipCode || ''}
        type="text"
        id="delivery-zipcode"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.delivery.country`)}
        inputSize={inputSize}
        onChange={handleDeliveryAddressInputChange}
        name="country"
        value={deliveryAddressInputValues?.country || ''}
        type="text"
        id="delivery-country"
        required={true}
      />
    </div>
  );

  const contactInputFields = (
    <div className={fieldsClassName}>
      <Input
        label={translate(`${baseLabel}.contact.firstname`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="firstName"
        value={contactInputValues?.firstName || ''}
        type="text"
        id="contact-street"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.contact.lastname`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="lastName"
        value={contactInputValues?.lastName || ''}
        type="text"
        id="contact-lastname"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.contact.phonenumber`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="phoneNumber"
        value={contactInputValues?.phoneNumber || ''}
        type="text"
        id="contact-phonenumber"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.contact.email`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="email"
        value={contactInputValues?.email || ''}
        type="text"
        id="contact-email"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.contact.website`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="website"
        value={contactInputValues?.website || ''}
        type="text"
        id="contact-website"
        required={true}
      />
      <Checkbox
        inputSize={inputSize}
        id="contact-pdfinvoice"
        checked={usePdfInvoice}
        onChange={handleUsePdfInvoiceChange}
        label={translate(`${baseLabel}.contact.pdfInvoice`)}
        name="pdfInvoice"
        wrapperClassName="my-2"
      />
      {usePdfInvoice && (
        <Input
          label={translate(`${baseLabel}.contact.emailpdfInvoice`)}
          inputSize={inputSize}
          onChange={handleContactInputChange}
          name="emailPdfInvoice"
          value={contactInputValues?.emailPdfInvoice || ''}
          type="text"
          id="contact-emailpdfInvoice"
          required={usePdfInvoice}
        />
      )}
      <Input
        label={translate(`${baseLabel}.contact.businessdescription`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="businessDescription"
        value={contactInputValues?.businessDescription || ''}
        type="text"
        id="contact-businessdescription"
        required={true}
      />
      <Input
        label={translate(`${baseLabel}.contact.reference`)}
        inputSize={inputSize}
        onChange={handleContactInputChange}
        name="reference"
        value={contactInputValues?.reference || ''}
        type="text"
        id="contact-reference"
        required={true}
      />
    </div>
  );

  const economyContactEmailInputField = (
    <div className={fieldsClassName}>
      <Input
        label={translate(`${baseLabel}.economy.email`)}
        inputSize={inputSize}
        onChange={(e) => setEconomyContactEmail(e.target.value)}
        name="economyContactEmail"
        value={economyContactEmail || ''}
        type="text"
        id="economy-email"
        required={true}
      />
    </div>
  );

  return (
    <div className="container flex flex-col gap-4 pb-6 pt-4 md:gap-6 md:pb-8 md:pt-6 lg:pb-10">
      {model.title && (
        <div className="flex flex-col gap-2">
          <h1 className="uppercase">{model.title}</h1>
          {model.description && (
            <EditorString
              value={model.description.value}
              className="max-w-3xl text-lg [&_p]:leading-snug"
            />
          )}
        </div>
      )}
      {formSuccess && (
        <EditorString
          value={model.formSuccessMessage.value}
          className="text-lg"
        />
      )}
      {errorMessage.length > 0 && (
        <div className="text-lg">{errorMessage}</div>
      )}
      <form className="flex flex-col gap-6 lg:gap-8" onSubmit={submitForm}>
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:gap-10">
          <div className="flex flex-col gap-4 lg:gap-6">
            <div className="flex flex-col gap-2">
              <h4>{translate(`${baseLabel}.organization.title`)}</h4>
              {organizationInputFields}
            </div>
            <div className="flex flex-col gap-2">
              <h4>{translate(`${baseLabel}.economy.title`)}</h4>
              {economyContactEmailInputField}
            </div>
          </div>
          <div className="flex flex-col gap-4 lg:gap-6">
            <div className="flex flex-col gap-2">
              <h4>{translate(`${baseLabel}.billingaddress.title`)}</h4>
              {billingAddressInputFields}
            </div>
            <div className="flex flex-col gap-2">
              <h4>{translate(`${baseLabel}.deliveryaddress.title`)}</h4>
              {deliveryAddressInputFields}
            </div>
          </div>
          <div className="flex flex-col gap-4 lg:gap-6">
            <div className="flex flex-col gap-2">
              <h4>{translate(`${baseLabel}.contact.title`)}</h4>
              {contactInputFields}
            </div>
          </div>
        </div>
        <button className="btn btn--primary btn--large" type="submit">
          {translate(`${baseLabel}.button.send`)}
        </button>
      </form>
    </div>
  );
};

export default ApplicationForm;
